import { type MouseEvent, type ReactNode, useState } from 'react';
import type { Reservation } from '@repo/domain';
import {
  formatFlatPrice,
  useInvalidateWallet,
  usePaynowClauses,
  useReservationActions,
  useWallet,
} from '@repo/domain-service';
import { i18n, tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { PlusIcon } from '@repo/icons';
import { Button, CodeInputGroup } from '@repo/ui';
import { useNavigate } from 'react-router-dom';

import Header from '@features/reservation/components/Header';
import DevForcedOutcomeSelect from '@features/reservation/components/payment/DevForcedOutcomeSelect';
import PaymentMethodCard from '@features/reservation/components/payment/PaymentMethodCard';
import PaynowClauses from '@features/reservation/components/payment/PaynowClauses';
import { PaymentMethod, BLIK_LENGTH } from '@features/reservation/constants';
import { PAYMENT_METHODS } from '@features/reservation/constants/paymentMethods';
import {
  type ExtensionPayment,
  type FutureSpanPayment,
  type PaymentMethodConfig,
  type PaymentMethodId,
  type PaymentResultLocationState,
} from '@features/reservation/types';
import { trackReservationSuccess } from '@features/reservation/trackers/trackReservationResponse';
import { handleReservationError } from '@features/reservation/utils/handleReservationError';
import ProcessProgress from '@features/reservation/components/process/ProcessProgress';
import { type WalletTopUpLocationState } from '@features/account/types';
import { selectStatus, useAuthStore } from '@features/auth';

import { getConfig } from '@shared/env';
import PageFrame from '@shared/components/page/PageFrame';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  reservation: Reservation;
  /** Pays for this extension of the (active) reservation instead of the pending reservation itself. */
  extensionPayment?: ExtensionPayment;
  /** Pays for this future span of the (revolving) reservation instead of the pending reservation itself. */
  futureSpanPayment?: FutureSpanPayment;
  secondsToDeadline?: number;
  onExpire?: () => void;
  /** Called when an extension payment was accepted but still awaits confirmation (BLIK). */
  onExtensionPaymentInitiated?: () => void;
  /** Called when a future span payment was accepted but still awaits confirmation (BLIK). */
  onFutureSpanPaymentInitiated?: () => void;
}

export default function PaymentMethodsContents({
  reservation,
  extensionPayment,
  futureSpanPayment,
  secondsToDeadline,
  onExpire,
  onExtensionPaymentInitiated,
  onFutureSpanPaymentInitiated,
}: Props) {
  const navigate = useNavigate();
  const testId = TestId.root('reservation.paymentPage');

  const flatPrice =
    extensionPayment?.price ?? futureSpanPayment?.price ?? reservation.estimatedPrice ?? reservation.price;
  const {
    initiateReservationPayment,
    initiatingReservationPayment,
    initiateReservationSpanExtensionPayment,
    initiatingReservationSpanExtensionPayment,
    initiateReservationSpanPayment,
    initiatingReservationSpanPayment,
  } = useReservationActions();
  const initiatingPayment =
    initiatingReservationPayment || initiatingReservationSpanExtensionPayment || initiatingReservationSpanPayment;
  const isReservationPayment = !extensionPayment && !futureSpanPayment;
  const { paynowClauses, loadingPaynowClauses } = usePaynowClauses();
  const blikClausesReady = paynowClauses.length > 0;
  const { wallet, loadingWallet } = useWallet();
  const invalidateWallet = useInvalidateWallet();
  // The wallet needs a registered account (guests can't open the top-up pages either).
  const isGuest = useAuthStore(selectStatus) === 'guest';
  // The backend fails a wallet payment the balance can't cover, so don't offer it.
  const walletCoversPrice = !isGuest && !!wallet && !!flatPrice && wallet.balance.amount >= flatPrice.amount;

  const [pickedMethodId, setPickedMethodId] = useState<PaymentMethodId | undefined>(undefined);
  // Until the user picks one: the wallet when it covers the price, else BLIK when its clauses
  // are ready, else PayNow. Nothing is preselected while the wallet or the clauses load,
  // so BLIK doesn't grab focus and then lose it.
  const getDefaultMethodId = (): PaymentMethodId | undefined => {
    if (loadingWallet || loadingPaynowClauses) return undefined;
    if (walletCoversPrice) return PaymentMethod.Wallet;
    if (blikClausesReady) return PaymentMethod.Blik;
    return PaymentMethod.Paynow;
  };
  const defaultMethodId = getDefaultMethodId();
  const selectedMethodId = pickedMethodId ?? defaultMethodId;
  const [blikCode, setBlikCode] = useState<string>('');
  const [paymentDispatched, setPaymentDispatched] = useState<boolean>(false);
  const useMockRepository = getConfig('useMockRepository');
  const isConfirmPossible: boolean =
    (selectedMethodId === 'blik' && !!blikCode) ||
    selectedMethodId === 'paynow' ||
    (selectedMethodId === 'wallet' && walletCoversPrice);

  const formattedPrice = flatPrice ? formatFlatPrice(flatPrice, i18n.language) : '0zł';
  const forwardButtonLabel = tr('reservations:payment_page.confirm_and_pay') + ` • ${formattedPrice}`;

  const handleBack = () => {
    navigate(-1);
  };

  const submitExtensionPayment = async (
    paidExtension: ExtensionPayment,
    paymentMethodId: PaymentMethodId,
    enteredBlikCode: string
  ) => {
    setPaymentDispatched(true);
    try {
      const response = await initiateReservationSpanExtensionPayment({
        reservationSpanId: paidExtension.reservationSpanId,
        newEndAt: paidExtension.newEndAt,
        paymentMethod: paymentMethodId,
        blikCode: paymentMethodId === 'blik' ? enteredBlikCode : undefined,
      });

      if (response.paymentUrl) {
        window.location.assign(response.paymentUrl);
        return;
      }
      if (paymentMethodId === PaymentMethod.Wallet) {
        // Wallet payments settle synchronously — nothing to wait for.
        invalidateWallet();
        const state: PaymentResultLocationState = { product: 'parkingSpotRentalExtension' };
        navigate(ROUTE_PATHS[RouteName.RESERVATION_SUCCESS], { replace: true, state });
        return;
      }
      onExtensionPaymentInitiated?.();
    } catch (error) {
      setPaymentDispatched(false);
      handleReservationError(error, navigate, paymentMethodId);
    }
  };

  const submitFutureSpanPayment = async (
    paidFutureSpan: FutureSpanPayment,
    paymentMethodId: PaymentMethodId,
    enteredBlikCode: string
  ) => {
    setPaymentDispatched(true);
    try {
      const response = await initiateReservationSpanPayment({
        reservationSpanId: paidFutureSpan.reservationSpanId,
        paymentMethod: paymentMethodId,
        blikCode: paymentMethodId === 'blik' ? enteredBlikCode : undefined,
      });

      if (response.paymentUrl) {
        window.location.assign(response.paymentUrl);
        return;
      }
      if (paymentMethodId === PaymentMethod.Wallet) {
        // Wallet payments settle synchronously — nothing to wait for.
        invalidateWallet();
        const state: PaymentResultLocationState = { product: 'parkingSpotRentalExtension' };
        navigate(ROUTE_PATHS[RouteName.RESERVATION_SUCCESS], { replace: true, state });
        return;
      }
      onFutureSpanPaymentInitiated?.();
    } catch (error) {
      setPaymentDispatched(false);
      handleReservationError(error, navigate, paymentMethodId);
    }
  };

  const submitPayment = async (enteredBlikCode: string) => {
    if (!selectedMethodId) return;

    if (extensionPayment) {
      await submitExtensionPayment(extensionPayment, selectedMethodId, enteredBlikCode);
      return;
    }

    if (futureSpanPayment) {
      await submitFutureSpanPayment(futureSpanPayment, selectedMethodId, enteredBlikCode);
      return;
    }

    setPaymentDispatched(true);
    try {
      const response = await initiateReservationPayment({
        reservationId: reservation.id,
        paymentMethod: selectedMethodId,
        blikCode: selectedMethodId === 'blik' ? enteredBlikCode : undefined,
      });

      trackReservationSuccess({ paymentMethod: selectedMethodId, response });

      if (response.paymentUrl) {
        // Redirect-based payment (PayNow): hand off to the gateway URL.
        window.location.assign(response.paymentUrl);
      }
      if (selectedMethodId === PaymentMethod.Wallet) invalidateWallet();
      // BLIK / wallet: the payment was accepted but isn't settled yet. Stay put —
      // initiating invalidates reservations, so the payment-flow gateway
      // re-resolves and shows the "confirm in your bank" (or processing) screen.
    } catch (error) {
      setPaymentDispatched(false);
      handleReservationError(error, navigate, selectedMethodId);
    }
  };

  // Filling the last BLIK digit submits the payment immediately — no separate
  // tap on the confirm button.
  const handleBlikComplete = (code: string) => {
    setBlikCode(code);
    if (initiatingPayment || paymentDispatched) return;
    void submitPayment(code);
  };

  const handlePasteError = () => {
    toast.danger(tr('reservations:blik.clipboard_access_denied'), testId.append('clipboardError'));
  };

  const handlePaymentMethodSelection = (methodId: PaymentMethodId): void => {
    setPickedMethodId(methodId);
    if (methodId !== 'blik') setBlikCode('');
  };

  const handleConfirmPayment = async () => {
    if (!isConfirmPossible || paymentDispatched) return;
    await submitPayment(blikCode);
  };

  // Top up without leaving the flow: the top-up pages bring the user back here
  // (with the extension or future span being paid for, if any), pre-filled with what's missing.
  const handleWalletTopUp = (event: MouseEvent<HTMLButtonElement>) => {
    // The button sits inside the method card's label — don't let the click select the method.
    event.preventDefault();
    event.stopPropagation();
    const missingAmount = wallet && flatPrice ? flatPrice.amount - wallet.balance.amount : 0;
    const state: WalletTopUpLocationState = {
      topUpReturn: {
        pathname: ROUTE_PATHS[RouteName.RESERVATION_PAYMENT],
        state: isReservationPayment ? undefined : { extensionPayment, futureSpanPayment },
        suggestedAmount: missingAmount > 0 ? missingAmount : undefined,
      },
    };
    navigate(ROUTE_PATHS[RouteName.ACCOUNT_WALLET_TOP_UP], { state });
  };

  const getMethodActionButtons = (method: PaymentMethodConfig): ReactNode => {
    if (method.id !== PaymentMethod.Wallet || !wallet || isGuest) return method.actionButtons;
    return (
      <Button
        label={tr('reservations:payment_page.wallet_top_up')}
        leftIcon={PlusIcon}
        color="tertiary"
        size="sm"
        className="mr-3"
        testId={testId.append('walletTopUp')}
        onClick={handleWalletTopUp}
      />
    );
  };

  const getMethodSubtitle = (method: PaymentMethodConfig): string | undefined => {
    if (method.id === PaymentMethod.Wallet) {
      if (isGuest) return tr('reservations:payment_page.wallet_guest_disclaimer');
      return wallet
        ? tr('reservations:payment_page.wallet_balance', { balance: formatFlatPrice(wallet.balance, i18n.language) })
        : undefined;
    }
    return method.subtitleTranslationKey ? `${tr(method.subtitleTranslationKey)} *` : undefined;
  };

  return (
    <PageFrame
      testId={testId}
      onGoBack={handleBack}
      forwardButton={{
        label: forwardButtonLabel,
        disabled: !isConfirmPossible || paymentDispatched,
        pending: initiatingPayment || paymentDispatched,
        onClick: handleConfirmPayment,
      }}
    >
      {isReservationPayment && (
        <ProcessProgress
          currentStep={3}
          secondsToDeadline={secondsToDeadline}
          onExpire={onExpire}
          testId={testId.append('progress')}
        />
      )}

      <Header
        title={tr('reservations:payment_page.title')}
        subtitle={tr('reservations:payment_page.subtitle')}
        testId={testId.append('title')}
      />

      <div className="bg-primary flex flex-col gap-3 rounded-3xl p-4">
        {PAYMENT_METHODS.map((method) => (
          <PaymentMethodCard
            key={method.id}
            selectedMethod={selectedMethodId === method.id}
            onMethodSelection={() => handlePaymentMethodSelection(method.id)}
            icon={method.icon}
            title={tr(method.titleTranslationKey)}
            subtitle={getMethodSubtitle(method)}
            testId={testId.append(`${method.id}Method`)}
            actionButtons={getMethodActionButtons(method)}
            disabled={
              Boolean(method.disabled) ||
              (method.id === PaymentMethod.Blik && !blikClausesReady) ||
              (method.id === PaymentMethod.Wallet && !walletCoversPrice)
            }
          >
            {method.hasExpandableContent && method.id === PaymentMethod.Blik && (
              <>
                <CodeInputGroup
                  // Remount on (de)selection so `autoFocus` re-runs and lands the
                  // cursor on the first digit the moment BLIK is picked.
                  key={selectedMethodId === PaymentMethod.Blik ? 'blikInputActive' : 'blikInputInactive'}
                  testId={testId.append('blikInput')}
                  onComplete={handleBlikComplete}
                  variant="connected"
                  disabled={selectedMethodId !== PaymentMethod.Blik}
                  autoFocus={selectedMethodId === PaymentMethod.Blik}
                  length={BLIK_LENGTH}
                  label={method.labelTranslationKey ? tr(method.labelTranslationKey) : undefined}
                  required
                  onPasteError={handlePasteError}
                />
                {selectedMethodId === PaymentMethod.Blik && <PaynowClauses testId={testId.append('paynowClauses')} />}
              </>
            )}
          </PaymentMethodCard>
        ))}
      </div>

      {useMockRepository && <DevForcedOutcomeSelect testId={testId} />}
    </PageFrame>
  );
}
